import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
  useColorModeValue,
  Badge,
} from "@chakra-ui/react";
import React from "react";
import {
  FiCheckCircle,
  FiEye,
  FiArrowRight,
  FiStar,
  FiEdit2,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

/**
 * Simple, prominent prompt shown when user completes their profile
 * Much cleaner than complex celebration flow
 */
export default function ProfileCompletePrompt({
  onViewMasterResume,
  onDismiss,
  basicInfo = {},
}) {
  const navigate = useNavigate();

  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const subtextColor = useColorModeValue("gray.600", "gray.300");

  const handleViewResume = () => {
    if (onViewMasterResume) {
      onViewMasterResume();
    } else {
      navigate("/me/resume");
    }
  };

  const handleEditProfile = () => {
    navigate("/me");
  };

  return (
    <Box
      bg="linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)"
      p={6}
      borderRadius="xl"
      border="1px solid"
      borderColor="green.200"
      position="relative"
      _hover={{
        borderColor: "green.300",
        transform: "translateY(-1px)",
        shadow: "lg",
      }}
      transition="all 0.3s ease"
    >
      <Flex justify="space-between" align="center">
        <HStack spacing={4} flex={1}>
          {/* Success Icon */}
          <Box
            w={12}
            h={12}
            bg="green.500"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon as={FiCheckCircle} color="white" boxSize={6} />
          </Box>

          {/* Content */}
          <VStack align="start" spacing={2} flex={1}>
            <HStack spacing={3} align="center">
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                🎉 Profile Complete!
              </Text>
              <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
                100%
              </Badge>
            </HStack>
            
            <Text fontSize="md" color={subtextColor} lineHeight="1.5">
              Your master resume is ready! View your complete professional profile and start applying to jobs.
            </Text>

            <HStack spacing={4} fontSize="sm" color={subtextColor}>
              <HStack spacing={1}>
                <Icon as={FiStar} boxSize={3} />
                <Text>6 sections completed</Text>
              </HStack>
              <HStack spacing={1}>
                <Icon as={FiCheckCircle} boxSize={3} />
                <Text>Ready to use</Text>
              </HStack>
            </HStack>
          </VStack>
        </HStack>

        {/* Action Buttons */}
        <VStack spacing={2} flexShrink={0}>
          <Button
            bg="green.500"
            color="white"
            leftIcon={<Icon as={FiEye} />}
            onClick={handleViewResume}
            size="md"
            _hover={{
              bg: "green.600",
              transform: "translateY(-1px)",
              shadow: "md",
            }}
            transition="all 0.2s ease"
            fontWeight="medium"
            px={6}
          >
            View Master Resume
          </Button>
          
          <Button
            variant="outline"
            borderColor="green.300"
            color="green.600"
            leftIcon={<Icon as={FiEdit2} />}
            onClick={handleEditProfile}
            size="sm"
            _hover={{
              bg: "green.50",
              borderColor: "green.400",
            }}
            transition="all 0.2s ease"
            px={4}
          >
            Edit Profile
          </Button>
        </VStack>
      </Flex>

      {/* Optional dismiss button */}
      {onDismiss && (
        <Box
          position="absolute"
          top={2}
          right={2}
          cursor="pointer"
          onClick={onDismiss}
          p={1}
          borderRadius="md"
          _hover={{ bg: "gray.100" }}
          transition="all 0.2s ease"
        >
          <Text fontSize="lg" color="gray.400">×</Text>
        </Box>
      )}
    </Box>
  );
}